import { type FC, type ReactNode, type MouseEventHandler } from "react";
import clsx from "clsx";
import { CaretDownIcon, EditIcon, FilterIcon } from "../atoms/Icon";

/**
 * 共通テーブル（テナント設定タブ等の共通デザイン）
 *
 * 使い方:
 * <CommonTable>
 *     <thead>
 *         <tr>
 *             <CommonTableEditHeaderCell />
 *             <CommonTableHeaderCell sortable onSort={...}>雇用形態コード</CommonTableHeaderCell>
 *         </tr>
 *     </thead>
 *     <tbody>
 *         {rows.map((row) => (
 *             <CommonTableRow key={row.id}>
 *                 <CommonTableEditCell onClick={() => onEdit(row)} />
 *                 <CommonTableCell>{row.code}</CommonTableCell>
 *             </CommonTableRow>
 *         ))}
 *     </tbody>
 * </CommonTable>
 */

const COLOR_BORDER = "#D9D9D9";
const COLOR_HEADER_BG = "#E9F1F2";
const COLOR_TEXT = "#1A1A1A";

const HEADER_CELL_CLASS = clsx(
    "h-[44px] px-3 whitespace-nowrap",
    "bg-[#E9F1F2] text-center font-medium text-sm text-[#1A1A1A]",
    "border-r border-[#D9D9D9] last:border-r-0",
);

const CELL_CLASS = clsx(
    "h-[44px] px-3 whitespace-nowrap",
    "text-center font-normal text-sm text-[#1A1A1A]",
    "border-r border-[#D9D9D9] last:border-r-0",
);

interface CommonTableProps {
    children: ReactNode;
    className?: string;
    fullWidth?: boolean;
}

export const CommonTable: FC<CommonTableProps> = ({ children, className, fullWidth }) => (
    <div
        className={clsx(
            "flex max-w-full max-h-full flex-col",
            fullWidth ? "w-full" : "w-fit",
            "overflow-hidden rounded-lg",
            "border border-[#D9D9D9]",
            className,
        )}
    >
        <div className="min-h-0 flex-1 overflow-auto [&::-webkit-scrollbar-track]:mt-[44px]">
            <table className={clsx("border-collapse", fullWidth ? "w-full" : "min-w-max")}>
                {children}
            </table>
        </div>
    </div>
);

interface CommonTableHeaderCellProps {
    children?: ReactNode;
    sortable?: boolean;
    onSort?: (anchor: HTMLButtonElement) => void;
    /** 並び替え/絞り込みアイコンのアクティブ表示（trueでカレットを反転） */
    sortActive?: boolean;
    /** 並び替え方向（↑/↓ インジケータ表示用。null/未指定で非表示） */
    sortDirection?: "asc" | "desc" | null;
    /** 絞り込み中の選択件数（数値が指定されたら赤バッジ表示。0 でも表示。undefined で非表示） */
    filterCount?: number;
    filterable?: boolean;
    onFilter?: () => void;
    className?: string;
}

const SortArrowIcon: FC<{ direction: "asc" | "desc"; className?: string }> = ({
    direction,
    className,
}) => (
    <svg
        viewBox="0 0 12 16"
        className={className}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        {direction === "desc" ? (
            <>
                <path d="M6 2 V 13" />
                <path d="M2.5 10 L 6 13.5 L 9.5 10" />
            </>
        ) : (
            <>
                <path d="M6 14 V 3" />
                <path d="M2.5 6 L 6 2.5 L 9.5 6" />
            </>
        )}
    </svg>
);

export const CommonTableHeaderCell: FC<CommonTableHeaderCellProps> = ({
    children,
    sortable = false,
    onSort,
    sortActive = false,
    sortDirection = null,
    filterCount,
    filterable = false,
    onFilter,
    className,
}) => {
    const leftSlot = filterable ? (
        <button
            type="button"
            onClick={onFilter}
            aria-label="フィルター"
            className="shrink-0 rounded p-0.5 transition-colors hover:bg-black/5"
        >
            <FilterIcon className="h-4 w-4" />
        </button>
    ) : (
        <span className="h-4 w-4 shrink-0" aria-hidden="true" />
    );

    if (sortable) {
        return (
            <th className={clsx(HEADER_CELL_CLASS, className)}>
                <div className="flex w-full items-center">
                    {leftSlot}
                    <button
                        type="button"
                        onClick={(e) => onSort?.(e.currentTarget)}
                        className="flex flex-1 items-center gap-1"
                    >
                        <span className="flex-1 text-center">{children}</span>
                        {filterCount !== undefined && (
                            <span
                                className={clsx(
                                    "inline-flex h-4 min-w-[16px] items-center justify-center",
                                    "rounded-full bg-red-500 px-1",
                                    "text-[10px] font-bold leading-none text-white",
                                )}
                                aria-label={`絞り込み ${filterCount} 件`}
                            >
                                {filterCount}
                            </span>
                        )}
                        {sortDirection && (
                            <SortArrowIcon
                                direction={sortDirection}
                                className="h-4 w-4 shrink-0"
                            />
                        )}
                        <CaretDownIcon
                            className={clsx(
                                "h-4 w-4 shrink-0 transition-transform",
                                sortActive && "rotate-180",
                            )}
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </th>
        );
    }

    return (
        <th className={clsx(HEADER_CELL_CLASS, className)}>
            <div className="flex w-full items-center">
                {leftSlot}
                <span className="flex-1 text-center">{children}</span>
                <span className="h-4 w-4 shrink-0" aria-hidden="true" />
            </div>
        </th>
    );
};

interface CommonTableEditHeaderCellProps {
    label?: string;
    className?: string;
}

export const CommonTableEditHeaderCell: FC<CommonTableEditHeaderCellProps> = ({
    label = "編集",
    className,
}) => (
    <th className={clsx(HEADER_CELL_CLASS, "w-[72px]", className)}>{label}</th>
);

interface CommonTableRowProps {
    children: ReactNode;
    className?: string;
}

export const CommonTableRow: FC<CommonTableRowProps> = ({ children, className }) => (
    <tr className={clsx("bg-white border-t border-[#D9D9D9]", className)}>
        {children}
    </tr>
);

interface CommonTableCellProps {
    children?: ReactNode;
    className?: string;
    colSpan?: number;
}

export const CommonTableCell: FC<CommonTableCellProps> = ({ children, className, colSpan }) => (
    <td className={clsx(CELL_CLASS, className)} colSpan={colSpan}>
        {children}
    </td>
);

interface CommonTableEditCellProps {
    onClick?: MouseEventHandler<HTMLButtonElement>;
    disabled?: boolean;
    className?: string;
}

export const CommonTableEditCell: FC<CommonTableEditCellProps> = ({
    onClick,
    disabled = false,
    className,
}) => (
    <td className={clsx(CELL_CLASS, "w-[72px]", className)}>
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label="編集"
            className={clsx(
                "inline-flex h-8 w-8 items-center justify-center rounded",
                "text-brand-500 transition-colors",
                "hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40",
            )}
        >
            <EditIcon className="h-5 w-5" />
        </button>
    </td>
);

export const commonTableColors = {
    border: COLOR_BORDER,
    headerBg: COLOR_HEADER_BG,
    text: COLOR_TEXT,
} as const;
